import {
  parseMeetingLocalEntries,
  type MeetingLocalStorage,
} from '@cerebro-diem/core';
/** One owner document per transaction makes first-submit custody atomic across tabs. Media is private device data. */
export function createMeetingStorage(database = 'cerebro-meeting-imports') {
  const open = () =>
    new Promise<IDBDatabase>((resolve, reject) => {
      const req = indexedDB.open(database, 1);
      let failed = false;
      req.onupgradeneeded = () => {
        req.result.createObjectStore('owners');
        req.result.createObjectStore('files');
      };
      req.onerror = () => {
        failed = true;
        reject(
          new Error(
            'Meeting storage could not be opened; source input is still here',
          ),
        );
      };
      req.onblocked = () => {
        failed = true;
        reject(new Error('Meeting storage is blocked by another window'));
      };
      req.onsuccess = () => {
        if (failed) {
          req.result.close();
          return;
        }
        req.result.onversionchange = () => req.result.close();
        resolve(req.result);
      };
    });
  async function transaction<T>(
    storeName: string,
    mode: IDBTransactionMode,
    action: (store: IDBObjectStore, set: (result: T) => void) => void,
  ): Promise<T> {
    const db = await open();
    try {
      return await new Promise<T>((resolve, reject) => {
        let result: T, reason: unknown;
        const tx = db.transaction(storeName, mode, { durability: 'strict' });
        tx.oncomplete = () => resolve(result);
        tx.onabort = () =>
          reject(
            reason ??
              new Error(
                'Meeting storage could not commit; original sources are kept',
              ),
          );
        tx.onerror = () => {
          reason ??= tx.error;
        };
        try {
          action(tx.objectStore(storeName), (value) => {
            result = value;
          });
        } catch (error) {
          reason = error;
          tx.abort();
        }
        // Errors in request callbacks must abort rather than leave a successful empty document.
        tx.addEventListener('error', () => {
          reason ??= tx.error;
        });
        const abort = (error: unknown) => {
          reason = error;
          tx.abort();
        };
        callbacks.set(tx, abort);
      });
    } finally {
      db.close();
    }
  }
  const callbacks = new WeakMap<IDBTransaction, (error: unknown) => void>();
  const documents: MeetingLocalStorage = {
    read: (owner) =>
      transaction('owners', 'readonly', (store, set) => {
        const req = store.get(owner);
        req.onsuccess = () => {
          try {
            set(
              parseMeetingLocalEntries(
                req.result === undefined ? [] : req.result,
                owner,
              ),
            );
          } catch (error) {
            callbacks.get(store.transaction)!(error);
          }
        };
      }),
    mutate: (owner, change) =>
      transaction('owners', 'readwrite', (store, set) => {
        const req = store.get(owner);
        req.onsuccess = () => {
          try {
            const next = parseMeetingLocalEntries(
              change(
                parseMeetingLocalEntries(
                  req.result === undefined ? [] : req.result,
                  owner,
                ),
              ),
              owner,
            );
            store.put(next, owner);
            set(next);
          } catch (error) {
            callbacks.get(store.transaction)!(error);
          }
        };
      }),
  };
  const key = (owner: string, id: string) => `${owner}/${id}`;
  return {
    ...documents,
    keepFile: (owner: string, id: string, blob: Blob) =>
      transaction<void>('files', 'readwrite', (store, set) => {
        if (!blob.size) throw new Error('Empty meeting recording');
        store.add(blob, key(owner, id));
        set(undefined);
      }),
    readFile: (owner: string, id: string) =>
      transaction<Blob>('files', 'readonly', (store, set) => {
        const req = store.get(key(owner, id));
        req.onsuccess = () => {
          if (!(req.result instanceof Blob) || !req.result.size) {
            callbacks.get(store.transaction)!(
              new Error(
                'No retained recording on this device; choose the original file to retry',
              ),
            );
            return;
          }
          set(req.result);
        };
      }),
    removeFile: (owner: string, id: string) =>
      transaction<void>('files', 'readwrite', (store, set) => {
        store.delete(key(owner, id));
        set(undefined);
      }),
  };
}
export const meetingStorage = createMeetingStorage();
